import { normalizeShape, rebaseShapes, type Shape } from '@vd/annotation-core';
import type { LiveEvent } from '@vd/types';
import { shouldRepublishAnnotations } from './annotationPending';

/**
 * The server's shapes from a live event this canvas should take, or null.
 *
 * One annotation row per ticket: the snapshot holds shapes for every image in
 * the carousel, each scoped by its own `imageId`, so an event for any other
 * target is not this canvas's. Older than a version already seen is stale
 * (review round 7: a save's event can arrive after a delete's), and events
 * from older api builds carry no version and are taken as before.
 *
 * Whoever made the change, this person included. An edit from another tab or
 * device arrives under their own name, and this used to be skipped unless it
 * added a shape, so a move or a deletion made elsewhere was ignored here and
 * the next save from here put it back. The event this canvas's own save
 * causes needs no special case: merging it changes nothing, since the
 * canvas already holds it (see `mergeRemoteAnnotations`).
 */
export function remoteAnnotationUpdate(
  event: LiveEvent,
  ticketId: string,
  lastVersion: number,
): { remote: Shape[]; version: number | undefined } | null {
  if (event.type !== 'annotation.updated') return null;
  if (event.ticketId !== ticketId || event.targetId !== ticketId) return null;
  if (event.version !== undefined && event.version <= lastVersion) return null;
  // Normalized like the local load: another client on an older build may
  // have saved shapes without `imageId`.
  const remote = Array.isArray(event.snapshot)
    ? (event.snapshot as Shape[]).map((shape) => normalizeShape(shape))
    : [];
  return { remote, version: event.version };
}

/**
 * The server's shapes as the new base, with what was drawn here since the old
 * base kept on top.
 *
 * `republish` is whether local work survived the merge and still has to reach
 * the server. When none did, the working copy IS the server's list, the same
 * array: the upload effect skips a save while the two are identical, and an
 * equal copy would pass that check and save the same shapes again the next
 * time an unrelated dependency changed.
 */
export function mergeRemoteAnnotations(
  remote: Shape[],
  current: Shape[],
  base: Shape[],
): { base: Shape[]; working: Shape[]; republish: boolean } {
  const merged = rebaseShapes(remote, current, base);
  const republish = shouldRepublishAnnotations(remote, merged);
  return { base: remote, working: republish ? merged : remote, republish };
}

/**
 * A copy of the server's shapes, merged into this canvas: somebody else's
 * change against the canvas's base, and this person's own save against the
 * basis it was made from (`ownBasis`, from `ownSaveBasis`).
 *
 * Its own save can come back while the base still predates it: its
 * announcement before its answer, or, after the ticket was left and opened
 * again, in a canvas that loaded from an older copy. Against that base, a
 * shape added and then undone here in the meantime reads as the server's
 * addition and comes back. Against its basis, what was done here since the
 * save, the undo included, is kept, and so is a teammate's change a conflict
 * retry folded into what the save stored: the same rule as the save's own
 * answer, which rebases the stored shapes against what was sent.
 */
export function receiveServerAnnotations(
  remote: Shape[],
  current: Shape[],
  base: Shape[],
  ownBasis: Shape[] | null,
): ReturnType<typeof mergeRemoteAnnotations> {
  return mergeRemoteAnnotations(remote, current, ownBasis ?? base);
}
